import type { Dispatch } from "react";
import type { EditorAction } from "../editor/reducers";
import type { ReshapeableRegionFragment, Vertex } from "../client/generated";
import { useState } from "react";
import { CSS_IN_TO_PX } from "../editor/utils";
import { usePageScale } from "../editor/hooks";
import { RegionReshapeHandle } from "./RegionReshapeHandle";
import { RegionPolygon } from "./RegionPolygon";
import { RegionShortcutsHandler } from "./RegionShortcutsHandler";
import { RegionSelectionHandler } from "./RegionSelectionHandler";
import { RegionMoveHandler } from "./RegionMoveHandler";
import { VertexInsertionHandle } from "./VertexInsertionHandle";
import { RegionLabel } from "./RegionLabel";
import { RegionCanvas } from "./RegionCanvas";
import { RegionContextMenuContent } from "./RegionContextMenuContent";
import { useUpdateRegionVerticesMutation } from "../client/generated";
import {
  ContextMenu,
  ContextMenuTrigger,
} from "@acdc2/ui/components/context-menu";

type Props = {
  fragment: ReshapeableRegionFragment;
  isSelected: boolean;
  dispatch: Dispatch<EditorAction>;
};

export function ReshapeableRegion({ fragment, isSelected, dispatch }: Props) {
  const pageScale = usePageScale();
  const pageWidth = fragment.page.width * CSS_IN_TO_PX;
  const pageHeight = fragment.page.height * CSS_IN_TO_PX;
  const [vertices, setVertices] = useState(fragment.shape.vertices);
  const [isReshaping, setIsReshaping] = useState(false);

  const vertexInsertionPoints = isSelected
    ? getVertexInsertionPoints(vertices)
    : [];

  const [mutate] = useUpdateRegionVerticesMutation();

  const onDone = async () => {
    const { data, errors } = await mutate({
      variables: {
        regionId: fragment.id,
        vertices: vertices.map((vertex) => ({
          left: vertex.left,
          top: vertex.top,
        })),
      },
    });

    if (errors) {
      console.error("Error updating region vertices:", errors);
    } else if (data?.updateRegionShape.userErrors) {
      console.error(
        "User error updating region vertices:",
        data.updateRegionShape.userErrors,
      );
    }

    setVertices(
      data?.updateRegionShape.region?.shape.vertices ?? fragment.shape.vertices,
    );
  };

  const deleteVertex = async (index: number) => {
    const { data } = await mutate({
      variables: {
        regionId: fragment.id,
        vertices: vertices
          .filter((_, i) => i !== index)
          .map(({ top, left }) => ({ top, left })),
      },
    });

    if (data && !data.updateRegionShape.userErrors) {
      setVertices(
        data.updateRegionShape.region?.shape.vertices ??
          fragment.shape.vertices,
      );
    }
  };

  return (
    <ContextMenu>
      <RegionContextMenuContent dispatch={dispatch} fragment={fragment} />
      <RegionCanvas>
        <ContextMenuTrigger asChild>
          <RegionMoveHandler
            isSelected={isSelected}
            onDone={onDone}
            onMove={(movementX, movementY) => {
              setVertices((oldVertices) => {
                return oldVertices.map((vertex) => ({
                  left: vertex.left + movementX / pageWidth / pageScale,
                  top: vertex.top + movementY / pageHeight / pageScale,
                }));
              });
            }}
          >
            <RegionShortcutsHandler fragment={fragment} dispatch={dispatch}>
              <RegionSelectionHandler fragment={fragment} dispatch={dispatch}>
                <RegionPolygon
                  fragment={{ ...fragment, shape: { vertices } }}
                />
              </RegionSelectionHandler>
            </RegionShortcutsHandler>
          </RegionMoveHandler>
        </ContextMenuTrigger>

        {!isReshaping &&
          vertexInsertionPoints.map((midpoint, index) => (
            <VertexInsertionHandle
              key={index}
              cx={midpoint.left * pageWidth}
              cy={midpoint.top * pageHeight}
              onInsert={() => {
                setVertices((oldVertices) => {
                  const newVertices = [...oldVertices];
                  newVertices.splice(index + 1, 0, midpoint);
                  return newVertices;
                });
              }}
            />
          ))}

        {isSelected &&
          vertices.map((vertex, index) => (
            <RegionReshapeHandle
              key={index}
              cx={vertex.left * pageWidth}
              cy={vertex.top * pageHeight}
              onMove={(dx, dy) => {
                setIsReshaping(true);
                setVertices((oldVertices) => {
                  const newVertices = [...oldVertices];
                  newVertices[index] = {
                    left: oldVertices[index].left + dx / pageWidth / pageScale,
                    top: oldVertices[index].top + dy / pageHeight / pageScale,
                  };
                  return newVertices;
                });
              }}
              onDone={() => {
                setIsReshaping(false);
                onDone();
              }}
              onDelete={() => deleteVertex(index)}
            />
          ))}

        {!isSelected && (
          <RegionLabel
            fragment={{ ...fragment, shape: { ...fragment.shape, vertices } }}
          />
        )}
      </RegionCanvas>
    </ContextMenu>
  );
}

function getVertexInsertionPoints(vertices: Vertex[]) {
  return vertices.map((vertex, index) => {
    const nextIndex = (index + 1) % vertices.length;
    const nextVertex = vertices[nextIndex];
    return {
      left: (vertex.left + nextVertex.left) / 2,
      top: (vertex.top + nextVertex.top) / 2,
    };
  });
}
